import React, { useCallback, useState } from 'react'
import { Accept, useDropzone } from 'react-dropzone'
import CopaDropzonePanel from './CopaDropzonePanel'
import CopaFileRow from './CopaFileRow'
import { ErrorMessage } from 'src/components/atoms/ErrorMessage'

interface MultiFileDropzoneForCopaProps {
  files: File[]
  onFilesChange: (files: File[]) => void
  accept: Accept
  acceptAttr?: string
  title: string
  showDropzone?: boolean
  onShowDropzoneChange: (show: boolean) => void
  description?: string
  hint?: string
  maxFiles: number
  maxFileSizeBytes: number
  isValidFile: (file: File) => boolean
  rejectionHint: string
  maxFilesHint: string
  oversizedHint: (files: File[]) => string
}

/**
 * Multi-file sibling of `SingleFileDropzoneForCopa`. Files are held in the parent until submit,
 * so an abandoned form uploads nothing.
 */
const MultiFileDropzoneForCopa: React.FC<MultiFileDropzoneForCopaProps> = ({
  files,
  onFilesChange,
  accept,
  acceptAttr,
  title,
  showDropzone = false,
  onShowDropzoneChange,
  description,
  hint,
  maxFiles,
  isValidFile,
  rejectionHint,
  maxFilesHint,
  oversizedHint,
  maxFileSizeBytes,
}) => {
  const [error, setError] = useState<string | null>(null)

  const onDrop = useCallback(
    (incoming: File[]) => {
      setError(null)

      const alreadyAdded = new Set(files.map((file) => file.name))
      const deduped = incoming.filter((file) => !alreadyAdded.has(file.name))
      const supported = deduped.filter(isValidFile)
      const withinSize = supported.filter((file) => file.size <= maxFileSizeBytes)

      const oversized = supported.filter((file) => file.size > maxFileSizeBytes)
      const unsupported = deduped.filter((file) => !isValidFile(file))

      const remainingSlots = Math.max(0, maxFiles - files.length)
      const accepted = withinSize.slice(0, remainingSlots)

      if (withinSize.length > remainingSlots) {
        setError(maxFilesHint)
      } else if (oversized.length > 0) {
        setError(oversizedHint(oversized))
      } else if (unsupported.length > 0) {
        setError(rejectionHint)
      }

      if (accepted.length === 0) return

      onShowDropzoneChange(false)
      onFilesChange([...files, ...accepted])
    },
    [
      files,
      onFilesChange,
      isValidFile,
      maxFileSizeBytes,
      maxFiles,
      maxFilesHint,
      oversizedHint,
      rejectionHint,
      onShowDropzoneChange,
    ],
  )

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop, accept, multiple: true })

  const removeFile = useCallback(
    (target: File) => {
      setError(null)
      onFilesChange(files.filter((file) => file !== target))
    },
    [files, onFilesChange],
  )

  return (
    <>
      {showDropzone && (
        <CopaDropzonePanel
          rootProps={getRootProps()}
          inputProps={{ ...getInputProps(), ...(acceptAttr && { accept: acceptAttr }) }}
          isDragActive={isDragActive}
          title={title}
          description={description}
          hint={hint}
          onCancel={() => onShowDropzoneChange(false)}
          className="min-h-[160px]"
        />
      )}

      <ErrorMessage message={error ?? undefined} />

      {files.length > 0 && (
        <div className="flex w-full flex-col gap-2">
          {files.map((file) => (
            <CopaFileRow key={file.name} file={file} onRemove={() => removeFile(file)} />
          ))}
        </div>
      )}
    </>
  )
}

export default MultiFileDropzoneForCopa
